"use client";

import Link from "next/link";

export const Title = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="LowDoseNaltrexone w-full text-center text-sky-950 dark:text-white text-2xl font-medium font-satoshi leading-10">
            {children}
        </div>
    );
};
export const Heading = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="H6 w-full text-sky-950 dark:text-white text-xl font-medium font-satoshi">
            {children}
        </div>
    );
};
export const SubHeading = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="H6 w-full text-sky-950 dark:text-white text-lg font-medium font-satoshi">
            {children}
        </div>
    );
};
export const Body1 = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="w-full text-center text-gray-500 dark:text-gray-300 text-xs font-normal font-suisse leading-tight tracking-wide">
            {children}
        </div>
    );
};
export const Body2 = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="Body2 text-gray-500 dark:text-gray-300 text-sm font-normal font-suisse leading-snug tracking-tight">
            {children} {/* Reduced top margin */}
        </div>
    );
};
export const Section = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="Section w-full py-9 border-b border-black/10 dark:border-white/10 flex-col justify-start items-start gap-1 inline-flex">
            {children}
        </div>
    );
};
export const List = ({
    children,
    isOrdered = false, // false by default, making it an unordered list
}: {
    children: React.ReactNode;
    isOrdered?: boolean;
}) => {
    const ListTag = isOrdered ? "ol" : "ul"; // Determines tag based on isOrdered
    return (
        <ListTag
            className={`pl-5 space-y-1 ${isOrdered ? "list-decimal" : "list-disc"}`}
        >
            {children}
        </ListTag>
    );
};

export const ListItem = ({ children }: { children: React.ReactNode }) => {
    return (
        <li className="text-sm text-gray-500 dark:text-gray-300 font-suisse leading-snug">
            {children}
        </li>
    );
};
// Table Container
export const Table = ({ children }: { children: React.ReactNode }) => {
    return (
        <table className="min-w-full divide-y divide-gray-300">
            {children}
        </table>
    );
};

// Table Header
export const TableHead = ({ children }: { children: React.ReactNode }) => {
    return <thead className="bg-gray-50 dark:bg-gray-700">{children}</thead>;
};

// Table Body
export const TableBody = ({ children }: { children: React.ReactNode }) => {
    return (
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {children}
        </tbody>
    );
};

// Header Row
export const HeaderRow = ({ children }: { children: React.ReactNode }) => {
    return <tr>{children}</tr>;
};

// Table Cell in Header
export const HeaderCell = ({ children }: { children: React.ReactNode }) => {
    return (
        <th
            scope="col"
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
        >
            {children}
        </th>
    );
};

// Body Row
export const TableRow = ({ children }: { children: React.ReactNode }) => {
    return <tr>{children}</tr>;
};

// Table Cell in Body
export const TableCell = ({ children }: { children: React.ReactNode }) => {
    return (
        <td className="px-6 py-4 whitespace-normal text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
            {children}
        </td>
    );
};

export const WebsiteLink = ({
    children,
    href,
}: {
    children: React.ReactNode;
    href: string;
}) => {
    return (
        <Link
            href={href}
            className="text-rthm-dark-blue dark:text-blue-400 hover:underline"
            target="_blank"
            rel="noreferrer"
        >
            {children}
        </Link>
    );
};
